import { useCallback, useState } from 'react';

import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';
import {
  useCreateFinanceCashAccount,
  useDeleteFinanceCashAccount,
  useFinanceCashAccounts,
  useUpdateFinanceCashAccount,
} from '../../../hooks/useSupplierLedger';
import { CASH_ACCOUNTS_ITEMS_PER_PAGE } from '../constants/cashAccountsConstants';
import { filterCashAccounts } from '../utils/cashAccountsUtils';

const useCashAccountsController = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const { data: accounts = [], isLoading } = useFinanceCashAccounts();
  const createAccount = useCreateFinanceCashAccount();
  const updateAccount = useUpdateFinanceCashAccount();
  const deleteAccount = useDeleteFinanceCashAccount();

  const filterFn = useCallback(filterCashAccounts, []);
  const pagination = usePaginatedFilter({
    data: accounts,
    itemsPerPage: CASH_ACCOUNTS_ITEMS_PER_PAGE,
    filterFn,
  });

  const openCreateDrawer = () => {
    setSelectedAccount(null);
    setEditMode(false);
    setDrawerOpen(true);
  };

  const openEditDrawer = (account) => {
    setSelectedAccount(account);
    setEditMode(true);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditMode(false);
    setSelectedAccount(null);
  };

  const createCashAccount = (payload) => createAccount.mutateAsync(payload);

  const updateCashAccount = (id, payload) => updateAccount.mutateAsync({ id, data: payload });

  const requestDelete = (account) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  const cancelDelete = () => {
    setAccountToDelete(null);
    setDeleteDialogOpen(false);
  };

  const confirmDelete = async () => {
    if (!accountToDelete) return;
    await deleteAccount.mutateAsync(accountToDelete.uuid);
    cancelDelete();
  };

  return {
    accountToDelete,
    closeDrawer,
    confirmDelete,
    cancelDelete,
    createCashAccount,
    deleteDialogOpen,
    drawerOpen,
    editMode,
    isDeleting: deleteAccount.isPending,
    isLoading,
    isSubmitting: createAccount.isPending || updateAccount.isPending || deleteAccount.isPending,
    openCreateDrawer,
    openEditDrawer,
    pagination,
    requestDelete,
    selectedAccount,
    updateCashAccount,
  };
};

export default useCashAccountsController;
